import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';
import { DateFormatService } from '@shared/services/date-format.service';

/**
 * Custom Date Adapter for Material Datepicker
 * Respects user's date format preferences and provides strict parsing
 * This prevents Material from misinterpreting dates based on browser locale
 */
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {
  constructor(private dateFormatService: DateFormatService) {
    super();
    // Use neutral locale, we'll handle formatting ourselves. In Angular Material
    // v22 the NativeDateAdapter constructor no longer accepts a locale argument
    // (locale is injected), so we force it explicitly via setLocale().
    this.setLocale('en-US');
  }

  /**
   * Parse date from user input
   * This is called when the user types a date manually in a Material datepicker input.
   * `NativeDateAdapter.parse` is typed as `any` upstream — use `unknown` here and
   * narrow to `string` before our format-aware path.
   */
  override parse(value: unknown): Date | null {
    if (!value || typeof value !== 'string') {
      return super.parse(value);
    }

    // Use our strict parsing that respects user preferences
    const parsedDate = this.dateFormatService.parseUserFormatString(value);

    return parsedDate;
  }

  /**
   * Format date for display in the input field
   * This is called when a date is selected from the calendar
   */
  override format(date: Date, _displayFormat: object): string {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '';
    }

    // Use our formatting service that respects user preferences
    return this.dateFormatService.formatDate(date, 'short');
  }

  /**
   * Get the first day of the week (0 = Sunday, 1 = Monday, etc.)
   * Belgian/European format uses Monday as first day
   */
  override getFirstDayOfWeek(): number {
    const format = this.dateFormatService.getCurrentFormat();
    // European formats start week on Monday
    if (format === 'belgian' || format === 'european') {
      return 1; // Monday
    }
    return 0; // Sunday for US format
  }

  /**
   * Get the names of the months for the calendar
   */
  override getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    const format = this.dateFormatService.getCurrentFormat();
    const config = this.dateFormatService.getFormatConfig(format);
    
    const date = new Date(2000, 0, 1);
    const monthNames: string[] = [];
    
    for (let i = 0; i < 12; i++) {
      date.setMonth(i);
      monthNames.push(
        date.toLocaleDateString(config.locale, { month: style })
      );
    }
    
    return monthNames;
  }

  /**
   * Get the names of the days of the week for the calendar
   */
  override getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    const format = this.dateFormatService.getCurrentFormat();
    const config = this.dateFormatService.getFormatConfig(format);
    
    const date = new Date(2017, 0, 1); // Sunday
    const dayNames: string[] = [];
    
    for (let i = 0; i < 7; i++) {
      dayNames.push(
        date.toLocaleDateString(config.locale, { weekday: style })
      );
      date.setDate(date.getDate() + 1);
    }
    
    return dayNames;
  }
}


